import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState
} from 'react';

import { cloneDeep } from 'lodash-es';

import { TableSortOption } from '@evinced-private/ui-common';

import { TABLE_STATE_DEFAULT_SORT } from 'src/common/consts/TableStateDefaults';
import { emptyFilterOptions, IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { getTableIdPrefix } from 'src/common/helpers/TableStateHelper';
import { getFromLocalStorage, saveToLocalStorage } from 'src/common/services/api/LocalStorageApi';
import { Logger } from 'src/common/services/Logger';

import {
	CustomConfigTableKeys,
	CustomConfigTableType,
	getDefaultCustomState
} from './TablesCustomStateHelper';

interface ITablePagination {
	page: number;
	sizePerPage: number;
}

interface ITableStateValue<T> {
	filters: IFilterOptions;
	tableSort: Array<TableSortOption>;
	paginationInfo: ITablePagination;
	customParams?: T;
}

type ITableState = {
	[id: string]: ITableStateValue<CustomConfigTableType>;
};

const TABLES_STATE = 'TABLES-STATE';
const TABLES_STATE_VERSION = 'TABLES-STATE-VERSION';
const CURRENT_TABLES_STATE_VERSION = 1;
const DEFAULT_PAGE = 1;
const DEFAULT_SIZE_PER_PAGE = 10;

const getDefaultState = (tableIdPrefix: string): ITableStateValue<CustomConfigTableType> => {
	return {
		filters: emptyFilterOptions(tableIdPrefix),
		tableSort: TABLE_STATE_DEFAULT_SORT[tableIdPrefix] ?? null,
		paginationInfo: {
			page: DEFAULT_PAGE,
			sizePerPage: DEFAULT_SIZE_PER_PAGE
		},
		customParams: getDefaultCustomState(tableIdPrefix)
	};
};

export interface ITableStateContext {
	tablesState: ITableState;
	updateTableState: (
		tableId: string,
		newStateValue: ITableStateValue<CustomConfigTableType>
	) => void;
	getTableStateById: (tableId: string) => ITableStateValue<CustomConfigTableType>;
	onTablePageOrPageSizeChange: (tableId: string, pagination: ITablePagination) => void;
	onTableParamStatusChange: (
		tableId: string,
		paramKey: CustomConfigTableKeys,
		paramMethod: () => boolean
	) => void;
	onTableSort: (tableId: string, sort: TableSortOption[]) => void;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
}

// One-time, version-guarded migration of persisted table state. Runs only when
// the stored version is behind the current one, then records the new version so
// it never re-runs (and future, valid selections are preserved).
const migrateTableState = (state: ITableState): ITableState => {
	const storedVersion = getFromLocalStorage<number>(TABLES_STATE_VERSION) ?? 0;
	if (storedVersion >= CURRENT_TABLES_STATE_VERSION) {
		return state;
	}

	if (storedVersion < 1) {
		const migrated = Object.entries(state).reduce<ITableState>((acc, [id, tableState]) => {
			acc[id] =
				tableState?.filters && 'scans' in tableState.filters
					? { ...tableState, filters: { ...tableState.filters, scans: [] } }
					: tableState;
			return acc;
		}, {});
		saveToLocalStorage(TABLES_STATE_VERSION, CURRENT_TABLES_STATE_VERSION);
		return migrated;
	}

	saveToLocalStorage(TABLES_STATE_VERSION, CURRENT_TABLES_STATE_VERSION);
	return state;
};

const loadTableStateFromStorage = (): ITableState => {
	try {
		const storedState = getFromLocalStorage<ITableState>(TABLES_STATE) ?? {};
		return migrateTableState(storedState);
	} catch (err) {
		Logger.error('Failed to load table state from storage', err);
		return {};
	}
};

const TableStateContext = createContext({} as ITableStateContext);
export const useTableState = (): ITableStateContext => useContext(TableStateContext);

interface TableStateProviderProps {
	children: ReactNode;
}

export const TableStateProvider: FC<TableStateProviderProps> = ({ children }) => {
	const [tablesState, setTableState] = useState<ITableState>(() => loadTableStateFromStorage());

	const updateTableState = useCallback(
		(tableId: string, newStateValue: ITableStateValue<CustomConfigTableType>): void => {
			setTableState((prevTablesState) => {
				const updatedState = cloneDeep(prevTablesState);
				updatedState[tableId] = newStateValue;
				return updatedState;
			});
		},
		[]
	);

	const getTableStateById = useCallback(
		(tableId: string): ITableStateValue<CustomConfigTableType> => {
			const currentState = cloneDeep(tablesState);
			const tableIdPrefix = getTableIdPrefix(tableId);
			return currentState[tableId] || getDefaultState(tableIdPrefix);
		},
		[tablesState]
	);

	const onTablePageOrPageSizeChange = useCallback(
		(tableId: string, pagination: ITablePagination): void => {
			const tableState = getTableStateById(tableId);
			const newState = {
				...tableState,
				paginationInfo: { ...pagination }
			};
			updateTableState(tableId, newState);
		},
		[getTableStateById, updateTableState]
	);

	const onTableParamStatusChange = useCallback(
		(
			tableId: string,
			paramKey: keyof CustomConfigTableType,
			paramMethod: () => CustomConfigTableType[typeof paramKey]
		): void => {
			const tableState = getTableStateById(tableId);
			const newState = {
				...tableState,
				customParams: {
					...(tableState.customParams ?? {}),
					[paramKey]: paramMethod()
				} as CustomConfigTableType
			};
			updateTableState(tableId, newState);
		},
		[getTableStateById, updateTableState]
	);

	const onTableSort = useCallback(
		(tableId: string, sort: TableSortOption[]): void => {
			const tableState = getTableStateById(tableId);

			const newState = { ...tableState, tableSort: sort };

			updateTableState(tableId, newState);
		},
		[getTableStateById, updateTableState]
	);

	const onFilterChange = useCallback(
		(tableId: string, filters: IFilterOptions): void => {
			const tableState = getTableStateById(tableId);
			const paginationInfo = { ...tableState.paginationInfo, page: 1 };
			const newState = { ...tableState, filters, paginationInfo };

			updateTableState(tableId, newState);
		},
		[getTableStateById, updateTableState]
	);

	useEffect(() => {
		saveToLocalStorage(TABLES_STATE, tablesState);
	}, [tablesState]);

	const value = React.useMemo(
		() => ({
			tablesState,
			getTableStateById,
			updateTableState,
			onTablePageOrPageSizeChange,
			onTableParamStatusChange,
			onTableSort,
			onFilterChange
		}),
		[
			tablesState,
			getTableStateById,
			updateTableState,
			onTablePageOrPageSizeChange,
			onTableParamStatusChange,
			onTableSort,
			onFilterChange
		]
	);
	return <TableStateContext.Provider value={value}>{children}</TableStateContext.Provider>;
};
